import { useEffect, useId, useState } from 'react';
import {
  DUITNOW_BANKS,
  isDuitNowBankAvailable
} from '../../../../constants/duitnow.const';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue
} from '../../../../components/ui/select';
import { PaymentStepLayout } from './PaymentStepLayout';

const AVAILABLE_BANKS = DUITNOW_BANKS.filter((b) =>
  isDuitNowBankAvailable(b.code)
);
const COMING_SOON_BANKS = DUITNOW_BANKS.filter(
  (b) => !isDuitNowBankAvailable(b.code)
);

/** One line per bank, every row the same height; a long name ends in an ellipsis. */
const BANK_ROW = 'h-9 [&>span:last-child]:min-w-0 [&>span:last-child]:flex-1';

function BankName({ name }: Readonly<{ name: string }>) {
  return (
    <span className="block truncate whitespace-nowrap" title={name}>
      {name}
    </span>
  );
}

interface Props {
  initialBankCode?: string;
  onConfirm: (bankCode: string) => void;
  onBack: () => void;
  /** The screen Back returns to. */
  backLabel?: string;
  /** Reports every pick so the caller can restore it after Back → Continue. */
  onSelectionChange?: (bankCode: string) => void;
  isLoading?: boolean;
  noTopMargin?: boolean;
  /** A /pay failure for this step, shown inline; the selection is kept. */
  serverError?: string;
}

export function SelectDuitNowBankStep({
  initialBankCode,
  onConfirm,
  onBack,
  backLabel = 'Authorization Method',
  onSelectionChange,
  isLoading = false,
  noTopMargin = false,
  serverError
}: Readonly<Props>) {
  const [selected, setSelected] = useState<string | null>(
    initialBankCode && isDuitNowBankAvailable(initialBankCode)
      ? initialBankCode
      : null
  );
  // The server error disappears once the user acts on it, and a NEW error always shows.
  const [errorDismissed, setErrorDismissed] = useState(false);
  useEffect(() => {
    if (serverError) setErrorDismissed(false);
  }, [serverError]);
  const titleId = useId();
  const shownError = errorDismissed ? undefined : serverError;

  const pick = (code: string) => {
    setSelected(code);
    setErrorDismissed(true);
    onSelectionChange?.(code);
  };

  return (
    <PaymentStepLayout
      backLabel={backLabel}
      onBack={onBack}
      title="Select your bank"
      titleId={titleId}
      subtitle="Choose the bank you'll approve AutoDebit with."
      continueDisabled={selected === null}
      onContinue={() => selected && onConfirm(selected)}
      isLoading={isLoading}
      bottomBackLabel={`Go back to select ${backLabel}`}
      noTopMargin={noTopMargin}
    >
      {shownError && (
        <p role="alert" className="text-sm text-red-500">
          {shownError}
        </p>
      )}
      <Select value={selected ?? ''} onValueChange={pick}>
        <SelectTrigger
          aria-labelledby={titleId}
          className="focus:ring-none border-gray-300 text-gray-800 focus:ring-0"
        >
          <SelectValue placeholder="Choose your bank" />
        </SelectTrigger>
        <SelectContent
          side="bottom"
          avoidCollisions={false}
          collisionPadding={16}
          className="max-h-[min(340px,var(--radix-select-content-available-height))] [&>div[aria-hidden=true]]:hidden"
        >
          <SelectGroup>
            {AVAILABLE_BANKS.map((bank) => (
              <SelectItem
                key={bank.code}
                value={bank.code}
                className={BANK_ROW}
              >
                <BankName name={bank.name} />
              </SelectItem>
            ))}
          </SelectGroup>
          <SelectSeparator />
          <SelectGroup>
            <SelectLabel className="py-1 text-xs font-medium text-gray-500">
              Coming soon
            </SelectLabel>
            {COMING_SOON_BANKS.map((bank) => (
              <SelectItem
                key={bank.code}
                value={bank.code}
                disabled
                className={BANK_ROW}
              >
                <BankName name={bank.name} />
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </PaymentStepLayout>
  );
}
